import { AesMidasApi } from '@orbis-systems/orbis-api-clients/aesmidas';
import { BridgeWiseApi } from '@orbis-systems/orbis-api-clients/bridgewise';
import { InsightApi } from '@orbis-systems/orbis-api-clients/insight';
import { cloneDeep } from 'lodash-es';
import 'reflect-metadata';
import { container, delay, instanceCachingFactory } from 'tsyringe';
import { DeepPartial } from 'utility-types';
import {
    AccountApi,
    AmoApi,
    AmoMiddlewareApi,
    BranchApi,
    DocumentApi,
    GroupApi,
    MarketApi,
    ModelApi,
    NewsApi,
    QuoteApi,
    ResearchApi,
    StatApi,
    TipranksApi,
    TradingApi,
    UserApi,
    WebSocketApi,
} from './api';

import { WatchlistApi } from './api/WatchlistApi';
import { MemoryCache } from './Cache';
import { ApiInjectToken, MiddlewareInjectToken } from './constants/tokens';
import { OrbisApiWithPermissions } from './OrbisApiWithPermissions';
import { Apis, ApiVariant, CacheLevel, MiddlewareConfig } from './types';

export * from './constants/lookups';
export { OrbisApiPermissionError } from './OrbisApiWithPermissions';
export * from './types';
export * from './utils';

export class OrbisApiMiddleware {
    branch!: BranchApi;
    model!: ModelApi;
    group!: GroupApi;
    user!: UserApi;
    account!: AccountApi;
    market!: MarketApi;
    trading!: TradingApi;
    quote!: QuoteApi;
    watchlist!: WatchlistApi;
    research!: ResearchApi;
    stat!: StatApi;
    news!: NewsApi;
    tipranks!: TipranksApi;
    webSocket!: WebSocketApi;
    document!: DocumentApi;
    amo!: AmoApi;

    private config: MiddlewareConfig = {
        apiVariant: ApiVariant.Orbis,
        cacheDuration: 5000,
        cacheLevel: CacheLevel.RequestsAndCalculations,
        useAes: false,
        useMiddlewareAmo: false,
        apiPermissions: {
            advisor: false,
            rtbs: true,
            iav: true,
            documents: true,
            transfers: true,
            iavProcessorToken: true,
        },
    };

    constructor(
        private apis: Omit<Apis, 'insight' | 'bridgeWise' | 'aesMidas'>,
        config: DeepPartial<MiddlewareConfig>,
    ) {
        this.initialize(config);
    }

    private registerDependencies() {
        container.register(MiddlewareInjectToken.Config, {
            useFactory: () => this.config,
        });

        container.register(MiddlewareInjectToken.Cache, MemoryCache);

        container.register(MiddlewareInjectToken.OrbisApi, {
            useFactory: () => this.apis.orbis,
        });

        container.register(MiddlewareInjectToken.MiddlewareApi, {
            useFactory: () => this.apis.middleware,
        });

        container.register(MiddlewareInjectToken.InsightApi, {
            useFactory: () => {
                const config = cloneDeep(this.apis.middleware.getApiClient().getConfig());
                config.baseUrl += '/v1/insight';
                return new InsightApi(config);
            },
        });

        container.register(MiddlewareInjectToken.BridgeWiseApi, {
            useFactory: () => {
                const config = cloneDeep(this.apis.middleware.getApiClient().getConfig());
                config.baseUrl += '/v1/insight/bridgewise';
                return new BridgeWiseApi(config);
            },
        });

        container.register(MiddlewareInjectToken.UmaApi, {
            useFactory: () => {
                return this.apis.uma;
            },
        });

        const uma = this.apis.uma;

        container.register(MiddlewareInjectToken.AesMidasApi, {
            useFactory: () => {
                if (this.config.useAes && uma) {
                    const config = cloneDeep(uma.getApiClient().getConfig());
                    config.baseUrl += '/../../aes/api';
                    config.onRenewTokens = () => uma.renewTokens();
                    return new AesMidasApi(config);
                }

                return null;
            },
        });

        container.register(MiddlewareInjectToken.Apis, {
            useFactory: instanceCachingFactory<Apis>((container) => ({
                orbis: container.resolve(OrbisApiWithPermissions),
                middleware: container.resolve(MiddlewareInjectToken.MiddlewareApi),
                insight: container.resolve(MiddlewareInjectToken.InsightApi),
                bridgeWise: container.resolve(MiddlewareInjectToken.BridgeWiseApi),
                uma: container.resolve(MiddlewareInjectToken.UmaApi),
                aesMidas: container.resolve(MiddlewareInjectToken.AesMidasApi),
            })),
        });

        container.registerSingleton(
            ApiInjectToken.BranchApi,
            delay(() => BranchApi),
        );

        container.registerSingleton(ApiInjectToken.AccountApi, AccountApi);
        container.registerSingleton(ApiInjectToken.TradingApi, TradingApi);
        container.registerSingleton(ApiInjectToken.QuoteApi, QuoteApi);
        container.registerSingleton(ApiInjectToken.ModelApi, ModelApi);
        container.registerSingleton(ApiInjectToken.GroupApi, GroupApi);
        container.registerSingleton(ApiInjectToken.UserApi, UserApi);
        container.registerSingleton(ApiInjectToken.MarketApi, MarketApi);
        container.registerSingleton(ApiInjectToken.WatchlistApi, WatchlistApi);
        container.registerSingleton(ApiInjectToken.ResearchApi, ResearchApi);
        container.registerSingleton(ApiInjectToken.StatApi, StatApi);
        container.registerSingleton(ApiInjectToken.NewsApi, NewsApi);
        container.registerSingleton(ApiInjectToken.TipranksApi, TipranksApi);
        container.registerSingleton(ApiInjectToken.WebSocketApi, WebSocketApi);
        container.registerSingleton(ApiInjectToken.DocumentApi, DocumentApi);

        if (this.config.useMiddlewareAmo) {
            container.registerSingleton(ApiInjectToken.AmoMiddlewareApi, AmoMiddlewareApi);
        } else {
            container.registerSingleton(ApiInjectToken.AmoApi, AmoApi);
        }
    }

    initialize(config: DeepPartial<MiddlewareConfig>) {
        this.config = {
            ...this.config,
            ...config,
            apiPermissions: {
                ...this.config.apiPermissions,
                ...config.apiPermissions,
            },
        };

        container.reset();
        this.registerDependencies();

        this.branch = container.resolve(ApiInjectToken.BranchApi);
        this.model = container.resolve(ApiInjectToken.ModelApi);
        this.group = container.resolve(ApiInjectToken.GroupApi);
        this.user = container.resolve(ApiInjectToken.UserApi);
        this.account = container.resolve(ApiInjectToken.AccountApi);
        this.market = container.resolve(ApiInjectToken.MarketApi);
        this.trading = container.resolve(ApiInjectToken.TradingApi);
        this.quote = container.resolve(ApiInjectToken.QuoteApi);
        this.watchlist = container.resolve(ApiInjectToken.WatchlistApi);
        this.research = container.resolve(ApiInjectToken.ResearchApi);
        this.stat = container.resolve(ApiInjectToken.StatApi);
        this.news = container.resolve(ApiInjectToken.NewsApi);
        this.tipranks = container.resolve(ApiInjectToken.TipranksApi);
        this.webSocket = container.resolve(ApiInjectToken.WebSocketApi);
        this.document = container.resolve(ApiInjectToken.DocumentApi);
        this.amo = container.resolve(
            this.config.useMiddlewareAmo ? ApiInjectToken.AmoMiddlewareApi : ApiInjectToken.AmoApi,
        );
    }

    getApis() {
        return container.resolve<Apis>(MiddlewareInjectToken.Apis);
    }
}
